import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const images = [
  "/images/g1.jpeg",
  "/images/g2.jpeg",
  "/images/g3.jpeg",
  "/images/g4.jpeg",
  "/images/g5.jpeg",
  "/images/g6.jpeg",
  "/images/g7.jpeg",
  "/images/g8.jpeg",
  "/images/g9.jpeg",
  "/images/g10.jpeg",
  "/images/g11.jpeg",
  "/images/g12.jpeg",
  "/images/g13.jpeg",
  "/images/g14.jpeg",
];

export default function Gallery() {
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (current === null) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [current]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (current === null) return;
      if (e.key === 'ArrowLeft') {
        setCurrent((prev) => (prev - 1 + images.length) % images.length);
      } else if (e.key === 'ArrowRight') {
        setCurrent((prev) => (prev + 1) % images.length);
      } else if (e.key === 'Escape') {
        setCurrent(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [current]);

  return (
    <section id="gallery" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#F8FBFC] to-white relative overflow-hidden">
      
      <div className="absolute inset-0">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-teal-50/40 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-teal-50/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-teal-100"
          >
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span className="text-teal-600 text-xs sm:text-sm font-semibold tracking-wide">OUR CLINIC</span>
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1E293B] mb-4"
          >
            Clinic <span className="bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] bg-clip-text text-transparent">Gallery</span>
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            whileInView={{ opacity: 1, width: "5rem" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] mx-auto rounded-full"
          />
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-gray-500 text-sm sm:text-base mt-4"
          >
            Take a virtual tour of Dr. Sainulabid's IQRA Multispeciality Dental Clinic
          </motion.p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
          {images.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: (i % 5) * 0.05 }}
              viewport={{ once: true }}
              onClick={() => setCurrent(i)}
              className="group cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 aspect-square">
                <img
                  src={img}
                  alt={`IQRA Multispeciality Dental Clinic gallery ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/400x400?text=Clinic+Image";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-3 sm:pb-4">
                  <span className="text-white text-[10px] sm:text-sm font-medium bg-black/50 px-2.5 sm:px-3 py-1 rounded-full">
                    View
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {current !== null && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50"
          onClick={() => setCurrent(null)}
        >
          <button 
            className="absolute top-4 sm:top-6 right-4 sm:right-6 text-white/80 hover:text-white text-2xl sm:text-3xl hover:scale-110 transition-all duration-300 z-10 bg-black/50 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center"
            onClick={() => setCurrent(null)}
            aria-label="Close gallery"
          >
            <FaTimes />
          </button>
          
          <button 
            className="absolute left-2 sm:left-4 md:left-8 text-white/80 hover:text-white text-3xl sm:text-4xl md:text-5xl hover:scale-110 transition-all duration-300 bg-black/50 rounded-full w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center"
            onClick={(e) => { 
              e.stopPropagation(); 
              setCurrent((prev) => (prev - 1 + images.length) % images.length);
            }}
            aria-label="Previous image"
          >
            <FaChevronLeft />
          </button>
          
          <motion.img 
            key={current}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            src={images[current]} 
            alt={`IQRA Multispeciality Dental Clinic gallery ${current + 1}`} 
            className="max-w-[90%] max-h-[85%] rounded-xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()} 
          />
          
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm">
            {current + 1} / {images.length}
          </div>
          
          <button 
            className="absolute right-2 sm:right-4 md:right-8 text-white/80 hover:text-white text-3xl sm:text-4xl md:text-5xl hover:scale-110 transition-all duration-300 bg-black/50 rounded-full w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center"
            onClick={(e) => { 
              e.stopPropagation(); 
              setCurrent((prev) => (prev + 1) % images.length);
            }}
            aria-label="Next image"
          >
            <FaChevronRight />
          </button>
        </motion.div>
      )}
    </section>
  );
}